import { type LoaderFunctionArgs, json } from '@remix-run/node'
import {
	type ClientLoaderFunctionArgs,
	useLoaderData,
	Link,
} from '@remix-run/react'
import { instance } from '@viz-js/viz'
import { BrainCircuit, NotebookIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '#app/components/ui/button.js'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'
import { ScrollArea, ScrollBar } from '#app/components/ui/scroll-area'
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table.js'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server'
import { cn } from '#app/utils/misc.tsx'
import { SceneGraphSchema } from '#app/utils/scene-graph.ts'
import db, { type ITranscriptLog } from '#app/utils/scribe-db'

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
	const userId = await getUserId(request)

	const url = new URL(request.url)
	const anonymous = url.searchParams.get('anonymous')
	const isAnonymous = anonymous === 'true'

	const target = await prisma.target.findUnique({
		where: { id: params.targetId },
		select: {
			sceneGraphDigraph: true,
			description: true,
			shortDescription: true,
			title: true,
			sceneGraphJson: true,
			id: true,
			TargetReveal: userId
				? {
						where: {
							userId: userId ?? '',
						},
					}
				: undefined,
		},
	})

	if (!target) {
		throw new Response('Not Found', { status: 404 })
	}

	const parsedSceneGraph = SceneGraphSchema.safeParse(
		JSON.parse(target.sceneGraphJson ?? '{}'),
	)

	if (!parsedSceneGraph.success) {
		console.error(parsedSceneGraph.error.errors)
	}

	return json({
		target,
		targetReveal: !userId ? [] : target.TargetReveal,
		sceneGraph: parsedSceneGraph.success ? parsedSceneGraph.data : null,
		isAnonymous: !userId,
		forceDisplayAnalysis: !userId && isAnonymous,
	})
}

export const clientLoader = async ({
	params,
	serverLoader,
}: ClientLoaderFunctionArgs) => {
	// Call the server loader
	const serverData = await serverLoader<typeof loader>()

	// Fetch data on the client
	let localImpressions: ITranscriptLog[] = []
	try {
		localImpressions = await db.getTranscriptLogsForTarget(
			params.targetId ?? '',
		)
		console.log('localImpressions', localImpressions)
	} catch (error) {
		console.error('Error fetching local impressions:', error)
	}

	// Return the combined data to expose through useLoaderData()
	return {
		...serverData,
		localImpressions,
	}
}

clientLoader.hydrate = true

export const HydrateFallback = () => {
	return <div>Loading...</div>
}

export default function TargetAnalysis() {
	const {
		target: { description, sceneGraphDigraph, shortDescription, id },
		targetReveal,
		sceneGraph,
		localImpressions,
		forceDisplayAnalysis,
	} = useLoaderData<typeof clientLoader>()
	const graphRef = useRef<HTMLDivElement>(null)
	const [error, setError] = useState<string | null>(null)

	const targetRevealed = targetReveal.length > 0 || forceDisplayAnalysis

	useEffect(
		function loadGraph() {
			if (sceneGraphDigraph && graphRef.current && targetRevealed) {
				instance()
					.then(viz => {
						try {
							const svg = viz.renderSVGElement(sceneGraphDigraph)
							graphRef.current?.replaceChildren(svg)
						} catch (err) {
							setError(
								`Failed to render SVG: ${err instanceof Error ? err.message : String(err)}`,
							)
						}
					})
					.catch(err => {
						setError(
							`Failed to render SVG: ${err instanceof Error ? err.message : String(err)}`,
						)
					})
			}
		},
		[sceneGraphDigraph, targetRevealed, graphRef],
	)

	const [expandedDescription, setExpandedDescription] = useState(false)

	return (
		<div className="p-4 text-secondary-foreground">
			<h2 className="mb-4 text-2xl font-bold">
				<BrainCircuit className="mr-2 inline-block h-6 w-6" /> Analysis
			</h2>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				{targetRevealed && sceneGraph ? (
					<>
						<div id="middle-column">
							<h3 className="mb-3 text-xl font-semibold">Description</h3>
							<p className="text-lg">
								{shortDescription}
								<Button
									variant="link"
									onClick={() => setExpandedDescription(true)}
								>
									Show Full Description
								</Button>
							</p>
							<div className="my-6 rounded-lg bg-gray-100 p-4 shadow">
								<h3 className="mb-3 text-xl font-semibold">Scene Graph</h3>
								<div className="grid grid-cols-2 gap-4">
									<div className="rounded bg-card p-3 shadow">
										<p className="text-lg font-medium">Entities</p>
										<p className="text-3xl font-bold">
											{sceneGraph.entities.length}
										</p>
									</div>
									<div className="rounded bg-card p-3 shadow">
										<p className="text-lg font-medium">Relationships</p>
										<p className="text-3xl font-bold">
											{sceneGraph.relationships.length}
										</p>
									</div>
									<div className="col-span-2 rounded bg-card p-3 shadow">
										<p className="text-lg font-medium">Main Subject</p>
										<p className="truncate text-xl font-bold">
											{sceneGraph.entities
												.filter(entity => entity.is_main_subject)
												.map(entity => entity.entity_title)
												.join(', ')}
										</p>
									</div>
								</div>
							</div>
						</div>
						<div id="right-column">
							<h3 className="mb-3 text-xl font-semibold">Session Evaluation</h3>
							{localImpressions.length === 0 ? (
								<p>No session impressions captured</p>
							) : (
								<Table className="border">
									<TableCaption>Session Evaluation</TableCaption>
									<TableHeader>
										<TableRow>
											<TableHead className="text-lg font-bold">
												<NotebookIcon className="mr-2 inline-block h-4 w-4" />{' '}
												Logbook
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{localImpressions
											.sort((a, b) => b.startedAt - a.startedAt)
											.map(impression => (
												<TableRow className="group" key={impression.id}>
													<TableCell>
														<span>
															{impression.text}{' '}
															{!targetRevealed
																? impression.similarities?.map(
																		_similarity => '🎯',
																	)
																: ''}
														</span>
														<p className="text-gray-400 opacity-0 group-hover:opacity-100">
															{new Date(
																impression.startedAt,
															).toLocaleTimeString()}
														</p>
														{targetRevealed && (
															<div className="flex flex-wrap gap-2 italic">
																{impression.similarities?.map(similarity => (
																	<div key={similarity}>🎯 {similarity}</div>
																))}
															</div>
														)}
													</TableCell>
												</TableRow>
											))}
									</TableBody>
								</Table>
							)}
						</div>
					</>
				) : null}
				{targetRevealed ? null : (
					<div>
						<p>
							Practice this target and reveal it to see a detailed analysis
							generated with AI artifacts.
						</p>
						<Button className="mt-4" asChild>
							<Link to={`/targets/${id}/feedback-loop`}>
								⛩︎ Practice Target Now
							</Link>
						</Button>
					</div>
				)}
			</div>

			<Dialog open={expandedDescription} onOpenChange={setExpandedDescription}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Description</DialogTitle>
					</DialogHeader>
					<DialogDescription>
						<ScrollArea scrollHideDelay={300} className="h-[500px] w-full">
							<div className="px-4">
								<p>{description}</p>
							</div>
						</ScrollArea>
					</DialogDescription>
				</DialogContent>
			</Dialog>

			{sceneGraphDigraph && targetRevealed ? (
				error ? (
					<p className={cn('text-red-500', 'italic')}>
						Scene Graph Error:{error}
					</p>
				) : (
					<ScrollArea className="h-[500px] w-full">
						<div data-testid="scenegraph" ref={graphRef}></div>
						<ScrollBar orientation="horizontal" />
					</ScrollArea>
				)
			) : targetRevealed ? (
				<p className={cn('text-gray-500', 'italic')}>
					No scene graph available
				</p>
			) : null}
		</div>
	)
}
